<template>
    <div
        :class="$style.footer"
        data-interface-section="footer"
    >
        <AppBadge
            v-if="!appStore.isNativeAppEnv"
            :class="$style.app_badge_override"
        />
        <div
            v-if="!appStore.isAppEnv"
            :class="[$style.links_group, $style.links_menu_highlight]"
        >
            <Typography
                is="ul"
                type="paragraph"
                size="xs"
                :class="$style.links"
            >
                <li
                    v-for="item in topLinks"
                    :key="item.title"
                >
                    <Link
                        :to="item.to"
                        :data-interface-target="item.dataInterfaceTarget || 'navigation-link'"
                        :class="$style.links_item"
                        color="white-60"
                    >
                        {{ item.title }}
                    </Link>
                </li>
            </Typography>
        </div>
        <div
            v-if="!appStore.isAppEnv"
            :class="$style.main_links"
        >
            <div :class="[$style.links_group, $style.links_topic_categories]">
                <Typography
                    is="ul"
                    type="paragraph"
                    size="xs"
                    :class="$style.links"
                >
                    <li
                        v-for="item in categoriesLinks"
                        :key="item.title"
                    >
                        <Link
                            :to="item.to"
                            :class="$style.links_item"
                            color="white-60"
                            data-interface-target="navigation-link"
                        >
                            {{ item.title }}
                        </Link>
                    </li>
                </Typography>
            </div>
            <div :class="$style.links_group">
                <Typography
                    is="ul"
                    type="paragraph"
                    size="xs"
                    :class="$style.links"
                >
                    <li
                        v-for="item in pagesLinks"
                        :key="item.title"
                    >
                        <Link
                            :externalTo="item.externalTo"
                            :to="item.to"
                            :target="item.target"
                            :class="$style.links_item"
                            color="white-60"
                            :data-interface-target="item.dataInterfaceTarget || 'navigation-link'"
                        >
                            {{ item.title }}
                        </Link>
                    </li>
                    <li>
                        <Link
                            externalTo="about:blank"
                            :class="$style.links_item"
                            color="white-60"
                            data-interface-target="privacy-choices"
                            @click.prevent="onPrivacyChoicesClick"
                        >
                            {{ $tr('navigation.privacy_choices') }}
                        </Link>
                    </li>
                </Typography>
            </div>
        </div>
        <Typography
            is="div"
            v-if="!appStore.isAppEnv"
            type="paragraph"
            size="xs"
            :class="$style.other"
        >
            <LocaleSwitchersList
                :languageLinks="props.languageLinks"
                :class="$style.language_override"
            />
            <Copyright
                v-if="!appStore.isAppEnv"
                :class="$style.copyright_link_override"
            />
        </Typography>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import Copyright from '@/components_new/Copyright.vue'
import Link from '@/components_new/Link.vue'
import LocaleSwitchersList from '@/components_new/LocaleSwitchers/LocaleSwitchersList.vue'
import Typography from '@/components_new/Typography.vue'
import { useUrl } from '@/composables/url-helpers'
import { useTr } from '@/composables/use-tr'
import { AvailableLocale } from '@/i18n/helpers'
import { useGeneralData } from '@/query/use-general-data'
import { ROUTE_NAMES } from '@/router/constants'
import { BLOG_PATH, NAVIGATION_ALL_GAMES_LINKS, NAVIGATION_LINKS, type NavLink } from '@/router/navigation-constants'
import { useAppStore } from '@/store/app-store'
import { LinkedCategory } from '@/types'

import AppBadge from './components/AppBadge.vue'

const props = defineProps<{
    languageLinks?: Record<AvailableLocale, string>
    additionalCategories?: LinkedCategory[]
}>()

const tr = useTr()
const appStore = useAppStore()

const { mainData } = useGeneralData()

const allGamesNavigation = computed(() => NAVIGATION_ALL_GAMES_LINKS(tr))
const menuHighlightCategoriesList = computed(() => mainData.value.menuHighlightCategoriesList || [])

const topLinks = computed<(NavLink & { dataInterfaceTarget?: string })[]>(() => [
    ...menuHighlightCategoriesList.value.map(({ title, location }) => ({
        title,
        to: location,
        dataInterfaceTarget: 'category',
    })),
    {
        title: tr('navigation.random'),
        to: { name: ROUTE_NAMES.RANDOM_GAME },
        dataInterfaceTarget: 'random-game',
    },
    {
        title: allGamesNavigation.value.allTags.title,
        to: allGamesNavigation.value.allTags.to,
    },
    {
        title: allGamesNavigation.value.allSeries.title,
        to: allGamesNavigation.value.allSeries.to,
    },
])

const { getCategoryRoute } = useUrl()

const menuCategoriesList = computed(() =>
    mainData.value.menuCategoriesList?.map(({ title, location }) => ({
        title,
        to: location,
    })),
)
const additionalCategoriesLinks = computed(() => {
    return props.additionalCategories?.map(({ title, hru, categoryType }) => ({
        title,
        to: getCategoryRoute({ hru, type: categoryType }),
    }))
})

const categoriesLinks = computed<NavLink[]>(() => [
    ...(additionalCategoriesLinks.value || menuCategoriesList.value || []),
    {
        title: allGamesNavigation.value.allGames.title,
        to: allGamesNavigation.value.allGames.to,
    },
])

const pagesLinks = computed<(NavLink & { dataInterfaceTarget?: string })[]>(() => [
    {
        title: tr('navigation.blog'),
        externalTo: BLOG_PATH,
        target: '_blank' as const,
    },
    ...NAVIGATION_LINKS(tr),
])

function onPrivacyChoicesClick() {
    window.googlefc?.showRevocationMessage?.()
}
</script>

<style module>
.footer {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    padding-bottom: 60px;
}

.language_override {
    padding-bottom: 16px;
}

.main_links,
.links_group,
.app_badge_override,
.other {
    grid-column: span 2;
}

.links_group {
    border-top: 1px solid var(--white-20);
    margin-top: 16px;
    padding-top: 16px;
}

.links {
    columns: 2 auto;
}

.links_item,
.copyright_link_override {
    display: block;
    padding: 4px 0;
}

.other {
    margin-top: 16px;
}

.copyright_link_override {
    margin-top: 24px;
}

@media (--tablet-plus) {
    .footer {
        grid-template-columns: repeat(5, 1fr);
    }

    .language_override {
        padding-bottom: 24px;
    }

    .links_group {
        border-top: none;
        margin-top: unset;
        padding-top: unset;
        grid-column: auto;
    }

    .links_menu_highlight {
        grid-column: span 5;
        column-count: 5;
    }

    .links_menu_highlight,
    .main_links {
        border-top: 1px solid var(--white-20);
        margin-top: 16px;
        padding-top: 16px;
    }

    .links_topic_categories {
        column-count: 4;
    }

    .main_links {
        padding-top: 24px;
        display: grid;
        grid-column: span 5;
        grid-template-columns: 4fr 1fr;
    }

    .links {
        columns: unset;
    }

    .other {
        margin-top: 24px;
        grid-column: span 5;
    }
}
</style>
